import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Gift, ArrowRight, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ExitIntentModalProps {
  title?: string;
  description?: string;
  offer?: string;
  enabled?: boolean;
}

const ExitIntentModal = ({
  title = "¡Espera! Tenemos algo para ti",
  description = "Antes de irte, déjanos tu email y recibe una consulta gratuita de 15 minutos",
  offer = "Consulta gratuita",
  enabled = true,
}: ExitIntentModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !hasTriggered) {
        const dismissed = sessionStorage.getItem("exitIntentDismissed");
        if (!dismissed) {
          setIsOpen(true);
          setHasTriggered(true);
        }
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [enabled, hasTriggered]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      await supabase.from("customer_interactions").insert({
        interaction_type: "exit_intent_signup",
        customer_email: email,
        source_page: window.location.pathname,
        metadata: { offer },
        status: "new",
      });

      toast.success("¡Gracias! Te contactaremos pronto");
      setIsOpen(false);
      sessionStorage.setItem("exitIntentDismissed", "true");
    } catch (error) {
      toast.error("Error al enviar. Inténtalo de nuevo");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismiss = () => {
    setIsOpen(false);
    sessionStorage.setItem("exitIntentDismissed", "true");
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDismiss}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-punto/20 rounded-full flex items-center justify-center">
              <Gift className="h-6 w-6 text-punto" />
            </div>
            <DialogTitle className="text-xl">{title}</DialogTitle>
          </div>
          <DialogDescription className="text-base">
            {description}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="p-4 bg-punto/10 rounded-lg border border-punto/20">
            <p className="text-sm font-medium text-punto flex items-center gap-2">
              <Gift className="h-4 w-4" />
              {offer}
            </p>
          </div>
          
          <Input
            type="email"
            placeholder="tu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="h-12"
          />
          
          <Button
            type="submit"
            className="w-full h-12 bg-punto hover:bg-punto/90"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Enviando..." : "Quiero mi consulta gratis"}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          
          <button
            type="button"
            onClick={handleDismiss}
            className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            No gracias, continuar navegando
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ExitIntentModal;
